import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore.js";
import { useCardSet } from "../contexts/CardSetContext.js";
import { TABLE_BACKGROUNDS, type TableBackgroundKey } from "../config.js";
import { savePreferences } from "../lib/preferences.js";
import { useGameStore } from "../store/useGameStore.js";
import { useI18n } from "../i18n/index.js";
import type { TranslationKey } from "../i18n/index.js";
import type { AnimationSpeed } from "../contexts/CardSetContext.js";

// ============================================================
// Hilfsfunktion: Sektion-Überschrift
// ============================================================
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3 mt-6 first:mt-0">
      {children}
    </h2>
  );
}

// Mapping Tischfarben-Key → i18n-Key
const BG_LABEL_KEYS: Record<TableBackgroundKey, TranslationKey> = {
  casinoGreen:    "bg.casinoGreen",
  filzGreen:      "bg.filzGreen",
  waldGreen:      "bg.waldGreen",
  pokerFelt:      "bg.pokerFelt",
  electricPurple: "bg.electricPurple",
  rebeccaPurple:  "bg.rebeccaPurple",
};

// ============================================================
// SettingsPage
// ============================================================
export function SettingsPage() {
  const navigate = useNavigate();
  const user        = useAuthStore((s) => s.user);
  const isLoading   = useAuthStore((s) => s.isLoading);
  const authError   = useAuthStore((s) => s.error);
  const clearError  = useAuthStore((s) => s.clearError);
  const updateEmail = useAuthStore((s) => s.updateEmail);
  const updatePassword = useAuthStore((s) => s.updatePassword);
  const { t } = useI18n();

  const {
    selectedSetId,
    selectedBackName,
    availableSets,
    setSelectedSet,
    tableBackground,
    setTableBackground,
    leftHanded,
    setLeftHanded,
    animationSpeed,
    setAnimationSpeed,
    getFaceUrl,
  } = useCardSet();

  const autoAce = useGameStore((s) => s.autoAce);
  const setAutoAce = useGameStore((s) => s.setAutoAce);

  // ── E-Mail ──────────────────────────────────────────────────
  const [newEmail, setNewEmail] = useState("");
  const [emailSuccess, setEmailSuccess] = useState(false);

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();
    setEmailSuccess(false);
    await updateEmail(newEmail.trim());
    if (!useAuthStore.getState().error) {
      setEmailSuccess(true);
      setNewEmail("");
    }
  }

  // ── Passwort ─────────────────────────────────────────────────
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);

  async function handlePwSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();
    setPwSuccess(false);
    await updatePassword(currentPw, newPw);
    if (!useAuthStore.getState().error) {
      setPwSuccess(true);
      setCurrentPw("");
      setNewPw("");
    }
  }

  // ── Grafik — live preview (kein DB-Save) ─────────────────────
  function handleSetChange(setId: string, backName?: string) {
    const manifest = availableSets.find((m) => m.id === setId);
    const back = backName ?? manifest?.defaultBack ?? "blue";
    setSelectedSet(setId, back);
    setGraficSaved(false);
  }

  function handleBackChange(backName: string) {
    setSelectedSet(selectedSetId, backName);
    setGraficSaved(false);
  }

  function handleBgChange(key: TableBackgroundKey) {
    setTableBackground(key);
    setGraficSaved(false);
  }

  function handleLeftHandedToggle() {
    setLeftHanded(!leftHanded);
    setGraficSaved(false);
  }

  function handleAutoAceToggle() {
    setAutoAce(!autoAce);
    setGraficSaved(false);
  }

  function handleSpeedChange(speed: AnimationSpeed) {
    setAnimationSpeed(speed);
    setGraficSaved(false);
  }

  // ── Grafik speichern ──────────────────────────────────────────
  const [graficSaving, setGraficSaving] = useState(false);
  const [graficSaved,  setGraficSaved]  = useState(false);

  async function handleSaveGrafik() {
    if (!user) return;
    setGraficSaving(true);
    try {
      await savePreferences(user.id, {
        cardSet:         selectedSetId,
        backName:        selectedBackName,
        tableBackground: tableBackground,
        leftHanded:      leftHanded,
        autoAce:         autoAce,
        animationSpeed:  animationSpeed,
      });
      setGraficSaved(true);
    } catch (err) {
      console.error("[Settings] savePreferences failed:", err);
    } finally {
      setGraficSaving(false);
    }
  }

  // ── Aktives Set-Manifest ──────────────────────────────────────
  const activeManifest = availableSets.find((m) => m.id === selectedSetId);

  const displaySets = availableSets.filter((m) =>
    ["classic3", "classic4", "classic5"].includes(m.id)
  );

  return (
    <div
      className="h-full overflow-y-auto text-white"
      style={{ background: "var(--table-bg, #4E6A54)" }}
    >
      {/* Header */}
      <header className="bg-black/20 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-sm transition-colors"
        >
          {t("settings.back")}
        </button>
        <h1 className="text-base font-semibold">{t("settings.title")}</h1>
      </header>

      <div className="max-w-sm mx-auto px-4 py-6">

        {/* ══ KONTO ══════════════════════════════════════════════ */}
        {user ? (
          <>
            <SectionHeader>{t("settings.account")}</SectionHeader>

            <div className="mb-4">
              <p className="text-xs text-white/50 mb-1">{t("settings.username")}</p>
              <p className="text-white/70 font-medium">{user.username}</p>
            </div>

            {authError && (
              <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
                {authError}
              </p>
            )}

            <div className="mb-5">
              <p className="text-xs text-white/50 mb-1">
                {t("settings.changeEmail")}
                <span className="ml-2 text-white/30">{t("settings.currentEmail", { email: user.email })}</span>
              </p>
              <form onSubmit={handleEmailSubmit} className="flex gap-2">
                <input
                  type="email"
                  placeholder={t("settings.newEmail")}
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setEmailSuccess(false); }}
                  required
                  className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20
                             text-white placeholder-white/30 text-sm
                             focus:outline-none focus:ring-2 focus:ring-white/30"
                />
                <button
                  type="submit"
                  disabled={isLoading || !newEmail.trim()}
                  className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm
                             disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {t("settings.change")}
                </button>
              </form>
              {emailSuccess && (
                <p className="text-green-400 text-xs mt-1">{t("settings.emailSent")}</p>
              )}
            </div>

            <div className="mb-2">
              <p className="text-xs text-white/50 mb-1">{t("settings.changePassword")}</p>
              <form onSubmit={handlePwSubmit} className="flex flex-col gap-2">
                <input
                  type="password"
                  placeholder={t("settings.currentPassword")}
                  value={currentPw}
                  onChange={(e) => { setCurrentPw(e.target.value); setPwSuccess(false); }}
                  required
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/20
                             text-white placeholder-white/30 text-sm
                             focus:outline-none focus:ring-2 focus:ring-white/30"
                />
                <input
                  type="password"
                  placeholder={t("settings.newPassword")}
                  value={newPw}
                  onChange={(e) => { setNewPw(e.target.value); setPwSuccess(false); }}
                  required
                  minLength={6}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/20
                             text-white placeholder-white/30 text-sm
                             focus:outline-none focus:ring-2 focus:ring-white/30"
                />
                <button
                  type="submit"
                  disabled={isLoading || !currentPw || !newPw}
                  className="py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm
                             disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t("settings.changePassword")}
                </button>
              </form>
              {pwSuccess && (
                <p className="text-green-400 text-xs mt-1">{t("settings.passwordChanged")}</p>
              )}
            </div>
          </>
        ) : (
          <>
            <SectionHeader>{t("settings.account")}</SectionHeader>
            <p className="text-sm text-white/50 mb-2">
              <button
                onClick={() => navigate("/login")}
                className="underline text-white/70 hover:text-white transition-colors"
              >
                {t("auth.loginBtn")}
              </button>
              {" "}{t("settings.loginToManage")}
            </p>
          </>
        )}

        {/* ══ GRAFIK ══════════════════════════════════════════════ */}
        <SectionHeader>{t("settings.graphics")}</SectionHeader>

        {!user && (
          <p className="text-xs text-white/40 mb-4">
            {t("settings.sessionOnly")}{" "}
            <button onClick={() => navigate("/login")} className="underline hover:text-white/60 transition-colors">
              {t("auth.loginBtn")}
            </button>
            {" "}{t("settings.loginToSave")}
          </p>
        )}

        <div className="mb-5">
          <p className="text-xs text-white/50 mb-2">{t("settings.cardSet")}</p>
          <div className="flex gap-3 flex-wrap">
            {displaySets.map((manifest) => {
              const isActive = manifest.id === selectedSetId;
              const faceUrl = `cards/sets/${manifest.id}/faces/spades-1.png`;
              return (
                <button
                  key={manifest.id}
                  onClick={() => handleSetChange(manifest.id)}
                  className={[
                    "flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all",
                    isActive
                      ? "border-white/70 bg-white/15"
                      : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40",
                  ].join(" ")}
                >
                  <img
                    src={faceUrl}
                    alt={manifest.name}
                    className="w-14 rounded-md shadow"
                    style={{ aspectRatio: "2/3", objectFit: "cover" }}
                  />
                  <span className="text-xs text-white/60">{manifest.id}</span>
                </button>
              );
            })}
          </div>
        </div>

        {activeManifest && activeManifest.backs.length > 0 && (
          <div className="mb-5">
            <p className="text-xs text-white/50 mb-2">{t("settings.cardBack")}</p>
            <div className="flex gap-2 flex-wrap">
              {activeManifest.backs.map((back) => {
                const isActive = back === selectedBackName;
                const backUrl = `cards/sets/${selectedSetId}/backs/${back}.png`;
                return (
                  <button
                    key={back}
                    onClick={() => handleBackChange(back)}
                    title={back}
                    className={[
                      "rounded-lg border-2 overflow-hidden transition-all",
                      isActive ? "border-white/70 scale-105" : "border-white/20 hover:border-white/40",
                    ].join(" ")}
                  >
                    <img
                      src={backUrl}
                      alt={back}
                      className="w-10 block"
                      style={{ aspectRatio: "2/3", objectFit: "cover" }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-5">
          <p className="text-xs text-white/50 mb-2">{t("settings.tableColor")}</p>
          <div className="grid grid-cols-3 gap-2">
            {(Object.entries(TABLE_BACKGROUNDS) as [TableBackgroundKey, { label: string; color: string }][]).map(
              ([key, { color }]) => {
                const isActive = key === tableBackground;
                return (
                  <button
                    key={key}
                    onClick={() => handleBgChange(key)}
                    className={[
                      "flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-left transition-all",
                      isActive
                        ? "border-white/70 bg-white/10"
                        : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40",
                    ].join(" ")}
                  >
                    <span
                      className="w-4 h-4 rounded-full shrink-0 border border-white/30"
                      style={{ background: color }}
                    />
                    <span className="text-xs text-white/80 leading-tight">{t(BG_LABEL_KEYS[key])}</span>
                  </button>
                );
              }
            )}
          </div>
        </div>

        {/* ── Linkshänder-Modus ── */}
        <div className="mb-5">
          <p className="text-xs text-white/50 mb-2">{t("settings.leftHanded")}</p>
          <button
            onClick={handleLeftHandedToggle}
            className={[
              "flex items-center gap-3 px-4 py-2.5 rounded-lg border-2 w-full text-left transition-all",
              leftHanded
                ? "border-white/70 bg-white/10"
                : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40",
            ].join(" ")}
          >
            {/* Toggle-Indicator */}
            <span className={[
              "w-10 h-5 rounded-full relative transition-colors shrink-0",
              leftHanded ? "bg-green-500/70" : "bg-white/20",
            ].join(" ")}>
              <span className={[
                "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all",
                leftHanded ? "left-5" : "left-0.5",
              ].join(" ")} />
            </span>
            <span className="text-xs text-white/60 leading-tight">{t("settings.leftHandedDesc")}</span>
          </button>
        </div>

        {/* ── Automatisches Ass ── */}
        <div className="mb-5">
          <p className="text-xs text-white/50 mb-2">{t("settings.autoAce")}</p>
          <button
            onClick={handleAutoAceToggle}
            className={[
              "flex items-center gap-3 px-4 py-2.5 rounded-lg border-2 w-full text-left transition-all",
              autoAce
                ? "border-white/70 bg-white/10"
                : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40",
            ].join(" ")}
          >
            <span className={[
              "w-10 h-5 rounded-full relative transition-colors shrink-0",
              autoAce ? "bg-green-500/70" : "bg-white/20",
            ].join(" ")}>
              <span className={[
                "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all",
                autoAce ? "left-5" : "left-0.5",
              ].join(" ")} />
            </span>
            <span className="text-xs text-white/60 leading-tight">{t("settings.autoAceDesc")}</span>
          </button>
        </div>

        {/* ── Fluggeschwindigkeit ── */}
        <div className="mb-5">
          <p className="text-xs text-white/50 mb-2">{t("settings.animationSpeed")}</p>
          <div className="flex gap-2">
            {(["slow", "normal", "fast"] as const).map((speed) => {
              const isActive = speed === animationSpeed;
              const labelKey = `settings.speed${speed[0]!.toUpperCase()}${speed.slice(1)}` as Parameters<typeof t>[0];
              return (
                <button
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  className={[
                    "flex-1 py-2 rounded-lg border-2 text-sm transition-all",
                    isActive
                      ? "border-white/70 bg-white/10 font-medium"
                      : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40",
                  ].join(" ")}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {user && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveGrafik}
              disabled={graficSaving || graficSaved}
              className="py-2 px-5 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {graficSaving ? t("settings.saving") : t("settings.saveGraphics")}
            </button>
            {graficSaved && (
              <span className="text-green-400 text-sm">{t("settings.graphicsSaved")}</span>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
